// src/services/performanceMonitor.js
// 성능 모니터링 서비스 - LCP 및 Core Web Vitals 추적

import { debugLog, errorLog } from '../utils/environment';

class PerformanceMonitor {
  constructor() {
    this.metrics = {
      lcp: null,
      fid: null,
      cls: null,
      fcp: null,
      ttfb: null
    };
    
    this.observers = [];
    this.isInitialized = false;
  }

  // 성능 모니터링 초기화
  init() {
    if (this.isInitialized) return;
    
    try {
      this.observeLCP();
      this.observeFID();
      this.observeCLS();
      this.observeFCP();
      this.observeTTFB();
      
      this.isInitialized = true;
      debugLog('[PerformanceMonitor] 성능 모니터링 초기화 완료');
    } catch (error) {
      errorLog('[PerformanceMonitor] 초기화 실패:', error);
    }
  }

  // LCP (Largest Contentful Paint) 관측
  observeLCP() {
    if (!window.PerformanceObserver) return;

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      
      this.metrics.lcp = {
        value: lastEntry.startTime,
        element: lastEntry.element,
        url: lastEntry.url,
        size: lastEntry.size,
        tagName: lastEntry.element?.tagName,
        src: lastEntry.element?.src,
        // [LCP 최적화] LCP 요소 상세 정보 추적
        width: lastEntry.element?.naturalWidth,
        height: lastEntry.element?.naturalHeight,
        isFirstCard: lastEntry.element?.src?.includes('00.png') && lastEntry.element?.src?.includes('quality=90'),
        isSecondCard: lastEntry.element?.src?.includes('00.png') && lastEntry.element?.src?.includes('quality=85')
      };
      
      debugLog('[PerformanceMonitor] LCP 측정:', this.metrics.lcp);
      
      // [LCP 최적화] LCP 요소가 첫 번째 카드인지 확인
      if (this.metrics.lcp.isFirstCard) {
        debugLog('[PerformanceMonitor] ✅ LCP가 첫 번째 카드로 안정화됨');
      } else if (this.metrics.lcp.isSecondCard) {
        debugLog('[PerformanceMonitor] ⚠️ LCP가 두 번째 카드로 측정됨 - 최적화 필요');
      } else {
        debugLog('[PerformanceMonitor] ℹ️ LCP가 다른 요소로 측정됨');
      }
    });
    
    observer.observe({ entryTypes: ['largest-contentful-paint'] });
    this.observers.push(observer);
  }

  // FID (First Input Delay) 관측
  observeFID() {
    if (!window.PerformanceObserver) return;

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        this.metrics.fid = {
          value: entry.processingStart - entry.startTime,
          target: entry.target
        };
        
        debugLog('[PerformanceMonitor] FID 측정:', this.metrics.fid);
      });
    });
    
    observer.observe({ entryTypes: ['first-input'] });
    this.observers.push(observer);
  }

  // CLS (Cumulative Layout Shift) 관측
  observeCLS() {
    if (!window.PerformanceObserver) return;

    let clsValue = 0;
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      
      this.metrics.cls = { value: clsValue };
      debugLog('[PerformanceMonitor] CLS 측정:', this.metrics.cls);
    });
    
    observer.observe({ entryTypes: ['layout-shift'] });
    this.observers.push(observer);
  }

  // FCP (First Contentful Paint) 관측
  observeFCP() {
    if (!window.PerformanceObserver) return;

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        this.metrics.fcp = {
          value: entry.startTime,
          name: entry.name
        };
        
        debugLog('[PerformanceMonitor] FCP 측정:', this.metrics.fcp);
      });
    });
    
    observer.observe({ entryTypes: ['paint'] });
    this.observers.push(observer);
  }

  // TTFB (Time to First Byte) 관측
  observeTTFB() {
    if (!window.PerformanceObserver) return;

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.entryType === 'navigation') {
          this.metrics.ttfb = {
            value: entry.responseStart - entry.requestStart,
            url: entry.name
          };
          
          debugLog('[PerformanceMonitor] TTFB 측정:', this.metrics.ttfb);
        }
      });
    });
    
    observer.observe({ entryTypes: ['navigation'] });
    this.observers.push(observer);
  }

  // 성능 지표 가져오기
  getMetrics() {
    return { ...this.metrics };
  }

  // 성능 리포트 생성
  generateReport() {
    const report = {
      timestamp: new Date().toISOString(),
      metrics: this.getMetrics(),
      userAgent: navigator.userAgent,
      connection: navigator.connection ? {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink
      } : null
    };
    
    debugLog('[PerformanceMonitor] 성능 리포트:', report);
    return report;
  }

  // 관측자 정리
  disconnect() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.isInitialized = false;
  }
}

const performanceMonitor = new PerformanceMonitor();
export default performanceMonitor;
